<template>
  <div class="view forget-password">
    <div class="container">
      <div v-if="setPassword" class="top">
        <div class="title">{{ $t('welcomeToKYC') }}</div>
        <div class="sub-title">{{ $t('setPassword') }}</div>
      </div>

      <div v-else-if="$route.query.email" class="top">
        <div class="title">{{ $t('forgotPassword') }}</div>
        <div class="sub-title">{{ $t('pleaseSetPassword') }}</div>
      </div>

      <div v-else class="top">
        <div class="title">{{ $t('forgotPassword') }}</div>
      </div>

      <div v-if="setPassword || $route.query.email" class="form-container">
        <input
          id="password"
          v-model="password"
          type="password"
          :class="{ incorrect: isValidated && password !== passwordConfirm }"
          class="input"
          name="password"
          :placeholder="$t('newPassword')"
          @click="reset"
        />
        <input
          id="passwordConfirm"
          v-model="passwordConfirm"
          type="password"
          :class="{ incorrect: isValidated && password !== passwordConfirm }"
          class="input"
          name="passwordConfirm"
          :placeholder="$t('confirmPassword')"
          @click="reset"
          @keydown.enter="validate"
        />
      </div>

      <div v-else class="form-container">
        <input
          id="email"
          v-model="email"
          type="text"
          :class="{ incorrect: !checkMail && isValidated }"
          class="input"
          name="email"
          :placeholder="$t('userEmail')"
          @keydown.enter="validate"
        />
      </div>

      <div v-if="errorMessage" class="error-message" :class="{ active: errorMessage }">
        {{ errorMessage }}
      </div>
      <div v-if="isEmailSend && checkMail" class="info">
        <div class="icon"></div>
        <div class="text">{{ $t('EmailSend') }}</div>
      </div>
      <div v-if="setPassword" class="password-checker">
        <div class="title">{{ $t('passwordShouldContains') }}</div>
        <div
          v-for="checker in passwordChecker"
          :key="checker.id"
          class="checker"
          :class="{
            valid: checker.function(),
            invalid: checkerErrors.includes(checker.id),
          }"
        >
          <div class="icon"></div>
          <div class="text">{{ checker.text }}</div>
        </div>
      </div>
      <div v-if="setPassword">
        <div
          v-for="(agreement, index) in legalAgreements"
          :key="index"
          class="agreement"
          :class="{ checked: legalAgreements[index].value }"
          :aria-checked="!!legalAgreements[index].value"
          tabindex="0"
          @click="
            e => {
              legalAgreements[index].value = !legalAgreements[index].value;
              e.currentTarget.blur();
            }
          "
          @keypress.enter="legalAgreements[index].value = !legalAgreements[index].value"
        >
          <div class="checkbox" :class="{ checked: legalAgreements[index].value }" :aria-checked="!!legalAgreements[index].value"></div>
          <label v-html="agreement.text"></label>
        </div>
      </div>
      <div
        v-if="!isEmailSend"
        class="button main-button register-button"
        :class="{ disabled: setPassword && legalAgreements.find(legal => !legal.value) }"
        @click="validate"
      >
        {{ $t('validate') }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    setPassword: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      password: '',
      passwordConfirm: '',
      email: '',
      passwordChecker: [
        {
          id: 0,
          text: this.$t('passChecker.numberOfCharacters', { numberOfCharacters: 12 }),
          function: this.minimumLetters,
        },
        {
          id: 1,
          text: this.$t('passChecker.oneSpecialCharacter'),
          function: this.oneSpecialCharacter,
        },
        {
          id: 2,
          text: this.$t('passChecker.oneNumber'),
          function: this.oneNumber,
        },
        {
          id: 3,
          text: this.$t('passChecker.oneUppercase'),
          function: this.hasUppercase,
        },
      ],
      isValidated: false,
      errorMessage: '',
      checkerErrors: [],
      isEmailSend: false,
      legalAgreements: [
        {
          text: this.$t('rgpd.cguvTextAgreement'),
          value: false,
        },
        {
          text: this.$t('rgpd.textAgreement'),
          value: false,
        },
      ],
    };
  },

  computed: {
    checkMail() {
      if (this.email === '') return true;
      const regMail = /^(\.|\+|[\w-])*@([a-z0-9]+(\.?[a-z0-9-]+)*?\.[a-z]{2,99}|(\d{1,5}\.){3}\d{1,3})(:\d{4})?$/;
      return regMail.test(this.email);
    },
  },

  mounted() {
    if (this.$route.query.token && !this.setPassword) {
      this.$router.push({
        name: 'forgetPassword',
        query: { email: ' ', token: this.$route.query.token },
      });
    }
  },

  methods: {
    async register() {
      const result = await this.$store.dispatch('register', {
        token: this.$route.query.token,
        password: this.passwordConfirm,
        legalAccepted: true,
      });
      if (result.status === 401) {
        this.$router.push({ name: 'expiredLink' });
      } else {
        await this.$store.dispatch('getUser', result.accessToken);
        this.$router.push('/');
      }
    },

    async forgetPassword() {
      this.isEmailSend = true;
      await this.$store.dispatch('forgetPassword', { email: this.email });
    },

    async setNewPassword() {
      try {
        await this.$store.dispatch('resetPassword', {
          password: this.passwordConfirm,
          token: this.$route.query.token,
        });

        this.$router.push('/login');
      } catch (e) {
        this.errorMessage = this.$t('anErrorOccured');

        if (e.response.status === 401) {
          this.$router.push({ name: 'expiredLink' });
        } else if (e.response.status === 400 && e.response.data.code === 400900) {
          this.errorMessage = this.$t('passwordNotCompliant', { numberOfCharacters: 12 });
        } else if (e.response.status === 409 && e.response.data.code === 409002) {
          this.errorMessage = this.$t('passwordShouldBeDifferentFromOldOne');
        }
      }
    },

    async validate() {
      this.errorMessage = '';
      this.isValidated = true;
      if (this.$route.query.token && !this.setPassword) {
        await this.checkValidation(this.setNewPassword);
        return;
      }
      if (this.setPassword) await this.checkValidation(this.register);
      else if (this.email !== '' && this.checkMail) await this.forgetPassword();
      else {
        this.errorMessage = this.$t('invalidEmail');
        this.isValidated = false;
      }
    },

    async checkValidation(callback) {
      this.checkerErrors = [];
      this.errorMessage = '';

      if (!this.minimumLetters()) this.checkerErrors.push(0);
      if (!this.oneSpecialCharacter()) this.checkerErrors.push(1);
      if (!this.oneNumber()) this.checkerErrors.push(2);
      if (!this.hasUppercase()) this.checkerErrors.push(3);

      if (this.password.length !== 0 && this.password === this.passwordConfirm) {
        await callback();
      } else if (this.password.length === 0 || this.passwordConfirm.length === 0) {
        this.errorMessage = this.$t('pleaseFillInAllFields');
      } else {
        this.errorMessage = this.$t('passDontMatch');
      }
    },

    minimumLetters() {
      return this.password.length >= 12;
    },

    oneSpecialCharacter() {
      return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(this.password);
    },

    oneNumber() {
      return /[0123456789]+/.test(this.password);
    },

    hasUppercase() {
      return this.password.toLowerCase() !== this.password;
    },

    reset() {
      this.checkerErrors = [];
      this.isValidated = false;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.view {
  background: colors.$main-color;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 20px;

  .container {
    max-width: calc(100% - 32px);
    width: 590px;
    height: auto;
    padding: 32px 96px;
    box-sizing: border-box;
    background: colors.$white-color;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(colors.$darkblue-color, 0.2);

    @media screen and (max-width: 700px) {
      padding: 48px 32px;
    }

    .info {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin: 12px 0 0 0;

      .icon {
        margin: 0 12px 0 0;
        position: relative;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        border: 2px solid colors.$lightblue-color;
        flex-shrink: 0;

        &:after {
          position: absolute;
          content: '';
          bottom: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 8px;
          background: colors.$lightblue-color;
        }

        &:before {
          position: absolute;
          content: '';
          top: 3px;
          left: calc(50% - 1px);
          right: calc(50% - 1px);
          height: 2px;
          background: colors.$lightblue-color;
        }
      }

      .text {
        color: colors.$lightblue-color;
        font-size: 14px;
        opacity: 0.8;
      }
    }

    .top {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      margin: 0 0 32px;

      .title {
        font-family: fonts.$title-font;
        color: colors.$darkblue-color;
        font-weight: 500;
        font-size: 24px;
        line-height: 29px;
        margin-bottom: 8px;
      }

      .sub-title {
        font-family: fonts.$default-font;
        font-size: 14px;
        color: colors.$grey-color;
      }
    }

    .form-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin: 0 0 16px;

      .input {
        width: 100%;
        height: 48px;
        margin: 0 0 16px;
        padding: 12px 16px;
        box-sizing: border-box;
        color: colors.$text-color;
        border: solid 1px colors.$lightgrey-color;
        border-radius: 4px;
        background: colors.$white-color;

        &:last-child {
          margin: 0;
        }

        &::placeholder {
          color: colors.$lightgrey-color;
        }

        &.incorrect {
          border-color: colors.$error-color;
        }
      }
    }

    .error-message {
      visibility: hidden;
      height: 32px;
      margin: -8px 0 8px;
      font-size: 12px;
      color: colors.$error-color;

      &.active {
        visibility: visible;
      }
    }

    .password-checker {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;

      .title {
        font-family: fonts.$default-font;
        font-size: 12px;
        line-height: 15px;
        margin-bottom: 16px;
      }

      .checker {
        display: flex;
        align-items: center;
        justify-content: center;

        .icon {
          margin: 0 12px 0 6px;
          font-size: 12px;
          color: colors.$grey-color;
          width: 4px;
          height: 10px;
          border: 2px solid colors.$grey-color;
          border-left: none;
          border-top: none;
          transform: rotate(45deg);
          opacity: 0.8;
        }

        .text {
          font-family: fonts.$default-font;
          font-weight: 500;
          font-size: 12px;
          color: colors.$grey-color;
          line-height: 23px;
          opacity: 0.8;
        }

        &.valid {
          .icon,
          .text {
            font-weight: 700;
            color: colors.$green-color;
            border-color: colors.$green-color;
            opacity: 1;
          }
        }

        &.invalid {
          .icon,
          .text {
            color: colors.$error-color;
            border-color: colors.$error-color;
            font-weight: 700;
            opacity: 1;
          }
        }
      }
    }

    .register-button {
      width: 0;
      margin: 32px auto 0;
    }
  }
}

.agreement {
  display: flex;
  margin-top: 24px;
  cursor: pointer;
  width: 100%;

  :deep(label) {
    color: colors.$text-color;
    transition: 0.3s ease;
    font-family: 'Roboto', sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;

    a {
      transition: 0.3s;
      text-decoration: underline;

      &:hover {
        color: colors.$main-color;
      }
    }
  }

  .checkbox {
    margin-right: 20px;
  }
}
</style>
